/* eslint-disable camelcase */
import { useState } from 'react';
import { Box, Text, Stack, Progress, ActionIcon, Paper, Divider, Loader, Button, Group, Title, Transition } from '@mantine/core';
import { IconX, IconStars, IconChevronDown, IconSearch, IconMoodSad } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { getAirlineByIata } from 'aircodes';
import callAzureFunction from 'utils/callAzureFunctions';
import ResultDialog from 'views/points/ResultDialog';
import { mileagePrograms, unavailableMileagePrograms } from 'utils/constants';
import { openInNewTab } from 'utils/card-helpers';
import { coral } from 'theme/colors';
import { semanticColors } from 'theme/semanticColors';
import { shadows } from 'theme/shadows';
import { radius } from 'theme/radius';

const getValidAirlines = (YAirlines) => {
  if (!YAirlines) return null;
  const allAirlines = YAirlines.split(',').map((i) => i.trim());
  const validAirline = allAirlines.length > 0 ? allAirlines[0] : 'aa';
  return getAirlineByIata(validAirline);
};

const MileageResultsDrawer = ({ origin, destination, date, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [results, setResults] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedResult, setSelectedResult] = useState({});
  const [openResultDialog, setOpenResultDialog] = useState(false);

  const handleScan = async () => {
    try {
      setIsLoading(true);
      const response = await callAzureFunction({
        url: 'search-mileage-deals/v2',
        method: 'post',
        data: {
          origin_airport: origin,
          destination_airport: destination,
          start_date: dayjs(date).format('YYYY-MM-DD')
        }
      });

      const raw = response.data.data ?? [];
      const parsed = raw
        .filter((res) => !unavailableMileagePrograms.includes(res.Source))
        .filter((res) => res.YDirect === true)
        .map((item) => ({
          id: item.ID,
          date: dayjs(item.Date).format('MMM D'),
          weekday: dayjs(item.Date).format('dddd'),
          source: item.Source,
          mileageCostEcon: Intl.NumberFormat('en-US').format(item.YMileageCost),
          seatsEcon: item.YRemainingSeats,
          logoURL: mileagePrograms[item.Source]?.logoURL,
          routeDestination: item.Route.DestinationAirport,
          routeOrigin: item.Route.OriginAirport,
          airline: item.YDirectAirlines ? getValidAirlines(item.YDirectAirlines)?.name : getValidAirlines(item.YAirlines)?.name,
          airlineLogo: item.YDirectAirlines ? getValidAirlines(item.YDirectAirlines)?.logo : getValidAirlines(item.YAirlines)?.logo,
          mileageURL: mileagePrograms[item.Source]?.composeURL({
            from: item.Route.DestinationAirport,
            to: item.Route.OriginAirport,
            date: item.Date
          })
        }));

      console.log(parsed);

      setResults(parsed);
      setHasFetched(true);

      if (!parsed.length) {
        enqueueSnackbar('No direct points deals found for this route.', {
          variant: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      }
    } catch {
      enqueueSnackbar('Failed to scan points deals.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpand = async (result) => {
    try {
      setIsLoadingDetail(true);
      const response = await callAzureFunction({
        url: 'search-mileage-details/v2',
        method: 'post',
        data: { trip_id: result.id }
      });

      const { booking_links, data } = response.data;
      const cheapest = data.reduce((acc, curr) => (!acc || curr.MileageCost < acc.MileageCost ? curr : acc), null);
      const { Carriers, DepartsAt, ArrivesAt, FlightNumbers, MileageCost } = cheapest;

      setSelectedResult({
        bookingLinks: booking_links,
        mileageCost: Intl.NumberFormat('en-US').format(MileageCost),
        flightTitle: `${getValidAirlines(Carriers)?.name ?? Carriers} ${FlightNumbers}`,
        flightLogoURL: getAirlineByIata(Carriers)?.logo,
        origin: result.routeOrigin,
        destination: result.routeDestination,
        departureTime: DepartsAt ? dayjs(DepartsAt).format('MMM DD | hh:mm A') : '',
        arrivalTime: ArrivesAt ? dayjs(ArrivesAt).format('MMM DD | hh:mm A') : ''
      });
      setOpenResultDialog(true);
    } catch {
      enqueueSnackbar('Failed to load flight details.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return (
    <>
      <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        {/* Header */}
        <Group justify="space-between" align="center" px="sm" py="xs" style={{ flexShrink: 0 }}>
          <Stack gap={2}>
            <Group gap={6} align="center">
              <IconStars size={16} color={coral[6]} />
              <Title order={4} c={coral[6]}>
                Points Scanner
              </Title>
            </Group>
            <Text size="xs" c={semanticColors.text.secondary}>
              {origin} → {destination} · {dayjs(date).format('MMM DD, YYYY')}
            </Text>
          </Stack>
          <ActionIcon size="sm" variant="subtle" onClick={onClose}>
            <IconX size={16} />
          </ActionIcon>
        </Group>

        <Divider />

        <Transition mounted={isLoading} transition="fade" duration={200}>
          {(styles) => <Progress style={styles} size="xs" value={100} animated />}
        </Transition>

        {/* Body */}
        <Box style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {!hasFetched && (
            /* Initial state */
            <Stack align="center" justify="center" gap="lg" py={48} style={{ textAlign: 'center' }}>
              <Box
                style={{
                  backgroundColor: coral[6],
                  borderRadius: '50%',
                  width: 64,
                  height: 64,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                }}
              >
                <IconStars size={32} color="white" />
              </Box>
              <Box>
                <Title order={4} mb={6}>
                  Scan for Points Deals
                </Title>
                <Text size="sm" c={semanticColors.text.secondary} style={{ maxWidth: 260, margin: '0 auto', lineHeight: 1.7 }}>
                  Find the best loyalty points redemptions for{' '}
                  <Text component="span" fw={600} c={semanticColors.text.primary}>
                    {origin} → {destination}
                  </Text>{' '}
                  on{' '}
                  <Text component="span" fw={600} c={semanticColors.text.primary}>
                    {dayjs(date).format('MMM DD, YYYY')}
                  </Text>
                  .
                </Text>
              </Box>
              <Button
                variant="filled"
                onClick={handleScan}
                disabled={isLoading}
                leftSection={<IconSearch size={16} />}
                radius={24}
                fw={600}
                px="lg"
              >
                Scan Now
              </Button>
            </Stack>
          )}

          {hasFetched && results.length === 0 && (
            /* Empty results */
            <Stack align="center" justify="center" gap="md" py={48} style={{ textAlign: 'center' }}>
              <IconMoodSad size={40} color={semanticColors.text.muted} />
              <Text size="sm" c={semanticColors.text.secondary}>
                No direct points deals found for this route.
              </Text>
              <Button size="xs" variant="subtle" onClick={handleScan} disabled={isLoading}>
                Try Again
              </Button>
            </Stack>
          )}

          {hasFetched && results.length > 0 && (
            /* Results */
            <Stack gap={12}>
              {results.map((result) => (
                <Paper
                  key={result.id}
                  withBorder
                  radius={radius.control}
                  style={{
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                >
                  <Box p="xs">
                    <Group gap={12} align="center">
                      {/* Info */}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate="end">
                          {result.airline}
                        </Text>
                        <Text size="xs" c={semanticColors.text.secondary}>
                          {result.date} · {result.weekday}
                        </Text>
                      </Box>

                      {/* Program logo */}
                      {result.logoURL && (
                        <Box
                          component="img"
                          src={result.logoURL}
                          style={{
                            height: 20,
                            width: 44,
                            objectFit: 'contain',
                            flexShrink: 0,
                            cursor: 'pointer',
                            opacity: 0.85
                          }}
                          onClick={() => openInNewTab(result.mileageURL)}
                          alt={result.source}
                          title="Book via mileage program"
                        />
                      )}

                      {/* Mileage cost */}
                      <Box style={{ textAlign: 'right', flexShrink: 0 }}>
                        <Text size="sm" fw={700} c={coral[6]} style={{ lineHeight: 1.2 }}>
                          {result.mileageCostEcon}
                        </Text>
                        <Text size="xs" c={semanticColors.text.secondary} style={{ fontSize: '0.65rem' }}>
                          pts · {result.seatsEcon}+ seats
                        </Text>
                      </Box>

                      {/* Expand */}
                      <Box style={{ flexShrink: 0 }}>
                        {isLoadingDetail ? (
                          <Loader size={18} color={coral[6]} />
                        ) : (
                          <ActionIcon size="sm" variant="subtle" onClick={() => handleExpand(result)} disabled={isLoadingDetail}>
                            <IconChevronDown size={20} color={coral[6]} />
                          </ActionIcon>
                        )}
                      </Box>
                    </Group>
                  </Box>
                </Paper>
              ))}

              <Button
                size="xs"
                variant="subtle"
                onClick={handleScan}
                disabled={isLoading}
                style={{ alignSelf: 'flex-end', fontSize: '0.72rem' }}
              >
                Re-scan
              </Button>
            </Stack>
          )}
        </Box>
      </Box>

      <ResultDialog isLoading={isLoadingDetail} open={openResultDialog} setOpen={setOpenResultDialog} result={selectedResult} />
    </>
  );
};

MileageResultsDrawer.propTypes = {
  origin: PropTypes.string.isRequired,
  destination: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default MileageResultsDrawer;
